import { pixelToHex, type Hex } from '@sd/sim';
import { MOUSE, Plane, Raycaster, Vector2, Vector3, type PerspectiveCamera } from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { HEX_SIZE, hexToWorld } from '../systemView';

/**
 * The strategy camera (#55, docs/design/ui-style.md): it starts close over
 * the capital and is free from there. Middle drag pans over the map plane
 * (#111: the left button now selects, also by a drag), the wheel zooms from
 * a city view to the whole system, Ctrl + middle drag or Q/E turn it; WASD
 * and the arrows pan, Home flies back to the capital. Right click orders.
 *
 * Keys are read by position (KeyboardEvent.code), so they are the same on
 * a Russian layout.
 */
const PAN_KEYS: Readonly<Record<string, readonly [number, number]>> = {
  KeyW: [0, 1],
  ArrowUp: [0, 1],
  KeyS: [0, -1],
  ArrowDown: [0, -1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};
const TURN_KEYS: Readonly<Record<string, number>> = { KeyQ: 1, KeyE: -1 };

/** Screen units of panning per second, as a share of the camera's distance. */
const PAN_SPEED = 0.9;
/** Map units per pixel of a two-finger swipe, as a share of the camera's distance. */
const SWIPE_PAN = 0.0016;
const TURN_SPEED = 1.6;

export class CameraRig {
  private readonly held = new Set<string>();
  /** The menus' backdrop is a film, not a map (#141): no mouse or keys move it. */
  private locked = false;
  private flight: { from: Vector3; to: Vector3; t: number } | null = null;
  private readonly ground = new Plane(new Vector3(0, 1, 0), 0);
  private readonly raycaster = new Raycaster();
  /** «Тачпад» (#162): two fingers pan the map, a pinch (the wheel with Ctrl) zooms. */
  private touchpad = false;

  constructor(
    private readonly camera: PerspectiveCamera,
    private readonly controls: OrbitControls,
    private readonly element: HTMLElement,
  ) {
    // The left button selects (a click, or a drag for a rectangle, #111): the middle
    // one pans, and turns the view with Ctrl or Shift held.
    controls.mouseButtons = { LEFT: null, MIDDLE: MOUSE.PAN, RIGHT: null };
    controls.screenSpacePanning = false;
    controls.zoomToCursor = true;
    window.addEventListener('keydown', (e) => {
      if (isTyping(e) || this.locked) return;
      if (e.code in PAN_KEYS || e.code in TURN_KEYS) this.held.add(e.code);
    });
    window.addEventListener('keyup', (e) => this.held.delete(e.code));
    // Ahead of OrbitControls' own wheel zoom: on the target, capture listeners run first.
    element.addEventListener(
      'wheel',
      (e) => {
        if (!this.touchpad || this.locked || e.ctrlKey) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.panBy(e.deltaX, e.deltaY);
      },
      { capture: true, passive: false },
    );
    window.addEventListener('blur', () => this.held.clear());
  }

  setTouchpad(on: boolean) {
    this.touchpad = on;
  }

  /** Pans by a swipe: the map follows the fingers, as a page scrolls. */
  private panBy(dx: number, dy: number) {
    const target = this.controls.target;
    const forward = target.clone().sub(this.camera.position).setY(0).normalize();
    const right = new Vector3(-forward.z, 0, forward.x);
    const k = this.camera.position.distanceTo(target) * SWIPE_PAN;
    const step = right.multiplyScalar(dx * k).add(forward.multiplyScalar(-dy * k));
    target.add(step);
    this.camera.position.add(step);
    this.flight = null;
  }

  /** Locks the camera to the script (menus) or hands it back to the player (a match). */
  lock(on: boolean) {
    this.locked = on;
    this.controls.enabled = !on;
    this.held.clear();
  }

  /** Moves the view so it looks at `point`, keeping height and angle; animated. */
  flyTo(point: Vector3) {
    this.flight = { from: this.controls.target.clone(), to: point.clone().setY(0), t: 0 };
  }

  flyToHex(h: Hex) {
    this.flyTo(hexToWorld(h));
  }

  /** Zooms by a factor around the target (the minimap's buttons). */
  zoom(factor: number) {
    const offset = this.camera.position.clone().sub(this.controls.target);
    const length = Math.min(
      this.controls.maxDistance,
      Math.max(this.controls.minDistance, offset.length() * factor),
    );
    this.camera.position.copy(this.controls.target).add(offset.setLength(length));
  }

  update(dt: number) {
    const target = this.controls.target;
    if (this.flight) {
      this.flight.t = Math.min(1, this.flight.t + dt / 0.45);
      const k = 1 - (1 - this.flight.t) ** 3;
      const next = this.flight.from.clone().lerp(this.flight.to, k);
      this.camera.position.add(next.clone().sub(target));
      target.copy(next);
      if (this.flight.t >= 1) this.flight = null;
    }
    let x = 0;
    let y = 0;
    let turn = 0;
    for (const code of this.held) {
      const pan = PAN_KEYS[code];
      if (pan) {
        x += pan[0];
        y += pan[1];
      }
      turn += TURN_KEYS[code] ?? 0;
    }
    if (x !== 0 || y !== 0) {
      // Forward is where the camera looks, flattened onto the map plane.
      const forward = target.clone().sub(this.camera.position).setY(0).normalize();
      const right = new Vector3(-forward.z, 0, forward.x);
      const distance = this.camera.position.distanceTo(target);
      const step = forward
        .multiplyScalar(y)
        .add(right.multiplyScalar(x))
        .normalize()
        .multiplyScalar(PAN_SPEED * distance * dt);
      target.add(step);
      this.camera.position.add(step);
      this.flight = null;
    }
    if (turn !== 0) {
      const offset = this.camera.position.clone().sub(target);
      offset.applyAxisAngle(new Vector3(0, 1, 0), turn * TURN_SPEED * dt);
      this.camera.position.copy(target).add(offset);
    }
  }

  /** The point of the map plane under a screen position, or null above the horizon. */
  groundAt(clientX: number, clientY: number): Vector3 | null {
    const rect = this.element.getBoundingClientRect();
    const ndc = new Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    return this.raycaster.ray.intersectPlane(this.ground, new Vector3());
  }

  hexAt(clientX: number, clientY: number): Hex | null {
    const point = this.groundAt(clientX, clientY);
    return point ? pixelToHex(point.x, point.z, HEX_SIZE) : null;
  }

  /** What the camera sees of the map plane: four corners, for the minimap. */
  footprint(top: number): Vector3[] {
    const rect = this.element.getBoundingClientRect();
    const corners: [number, number][] = [
      [rect.left, rect.top + top],
      [rect.right, rect.top + top],
      [rect.right, rect.bottom],
      [rect.left, rect.bottom],
    ];
    return corners.map(([cx, cy]) => this.groundAt(cx, cy)).filter((p): p is Vector3 => p !== null);
  }
}

function isTyping(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  return (
    !!target &&
    (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.isContentEditable)
  );
}
